/**
 * @file Route-local runtime state for the standalone Ordering board.
 */

const CLOSED_PAGE_SIZE = 30;
const ACTIVE_ITEM_LIMIT = 1000;

const orderingState = {
    buckets: [],
    activeItems: [],
    closedByBucket: new Map(),
    bucketLoadState: "idle",
    activeLoadState: "idle",
    bucketError: null,
    activeError: null,
    search: "",
    showClosed: false,
    authoritativeItemsById: new Map(),
    routeGeneration: 0,
};

/**
 * Returns the mutable domain-owned Ordering state.
 * @returns {object} Ordering route state.
 */
export function getOrderingState() {
    return orderingState;
}

/**
 * Starts a new route generation and resets all route-loaded data.
 * @returns {number} New generation token.
 */
export function beginOrderingRouteSession() {
    orderingState.routeGeneration += 1;
    orderingState.buckets = [];
    orderingState.activeItems = [];
    orderingState.closedByBucket = new Map();
    orderingState.bucketLoadState = "loading";
    orderingState.activeLoadState = "loading";
    orderingState.bucketError = null;
    orderingState.activeError = null;
    orderingState.search = "";
    orderingState.showClosed = false;
    orderingState.authoritativeItemsById = new Map();
    return orderingState.routeGeneration;
}

/**
 * Invalidates the active route session without retaining route data.
 * @returns {void}
 */
export function resetOrderingRouteState() {
    orderingState.routeGeneration += 1;
    orderingState.buckets = [];
    orderingState.activeItems = [];
    orderingState.closedByBucket = new Map();
    orderingState.bucketLoadState = "idle";
    orderingState.activeLoadState = "idle";
    orderingState.bucketError = null;
    orderingState.activeError = null;
    orderingState.search = "";
    orderingState.showClosed = false;
    orderingState.authoritativeItemsById = new Map();
}

/**
 * Returns whether an async/listener result still belongs to the active route.
 * @param {number} generation Route generation captured by the caller.
 * @returns {boolean} True when current.
 */
export function isOrderingRouteGenerationCurrent(generation) {
    return orderingState.routeGeneration === generation;
}

/**
 * Returns or initializes a bucket's closed-history pagination state.
 * @param {string} bucketId Ordering bucket document ID.
 * @returns {object} Closed-history state.
 */
export function getClosedBucketState(bucketId) {
    if (!orderingState.closedByBucket.has(bucketId)) {
        orderingState.closedByBucket.set(bucketId, {
            items: [],
            cursor: null,
            hasMore: true,
            loading: false,
            loaded: false,
            error: null,
            revision: 0,
            loadToken: 0,
        });
    }
    return orderingState.closedByBucket.get(bucketId);
}

/**
 * Invalidates query-loaded closed pages after active membership changes.
 * @returns {void}
 */
export function invalidateOrderingClosedPages() {
    orderingState.closedByBucket.forEach((page) => {
        page.items = [];
        page.cursor = null;
        page.hasMore = true;
        page.loading = false;
        page.loaded = false;
        page.error = null;
        page.revision += 1;
        page.loadToken += 1;
    });
}

/**
 * Removes externally reopened records from every loaded closed page.
 * @param {Set<string>} activeIds Current active item IDs.
 * @returns {void}
 */
export function removeActiveItemsFromClosedPages(activeIds) {
    orderingState.closedByBucket.forEach((page) => {
        page.items = page.items.filter((item) => !activeIds.has(item.id));
    });
}

/**
 * Records listener-backed item state used for optimistic reconciliation.
 * @param {Array<object>} items Authoritative items.
 * @returns {void}
 */
export function recordAuthoritativeOrderingItems(items) {
    (items || []).forEach((item) => {
        orderingState.authoritativeItemsById.set(item.id, {...item});
    });
}

/**
 * Finds an item across active and loaded closed state.
 * @param {string} itemId Ordering item id.
 * @returns {object|null} Item or null.
 */
export function findOrderingItem(itemId) {
    const active = orderingState.activeItems.find((item) => item.id === itemId);
    if (active) {return active;}
    for (const page of orderingState.closedByBucket.values()) {
        const closed = page.items.find((item) => item.id === itemId);
        if (closed) {return closed;}
    }
    return orderingState.authoritativeItemsById.get(itemId) || null;
}

/**
 * Inserts or replaces an item in the correct active/closed collection.
 * @param {object} item Ordering item.
 * @returns {void}
 */
export function placeOrderingItem(item) {
    if (!item?.id) {return;}
    orderingState.activeItems = orderingState.activeItems
        .filter((entry) => entry.id !== item.id);
    orderingState.closedByBucket.forEach((page) => {
        page.items = page.items.filter((entry) => entry.id !== item.id);
    });
    if (item.isClosedResolved === true) {
        const page = getClosedBucketState(item.listId);
        page.items.push(item);
    } else {
        orderingState.activeItems.push(item);
    }
}

/**
 * Removes an item from every route-local Ordering collection.
 * @param {string} itemId Ordering item id.
 * @param {{ authoritative?: boolean }} [options={}] Removal options.
 * @returns {void}
 */
export function removeOrderingItem(itemId, options = {}) {
    orderingState.activeItems = orderingState.activeItems
        .filter((entry) => entry.id !== itemId);
    orderingState.closedByBucket.forEach((page) => {
        page.items = page.items.filter((entry) => entry.id !== itemId);
    });
    if (options.authoritative === true) {
        orderingState.authoritativeItemsById.delete(itemId);
    }
}

export { ACTIVE_ITEM_LIMIT, CLOSED_PAGE_SIZE };
